import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePaymentVerification, useDonationStatus } from '@/hooks/usePaymentVerification';
import { Card, LoadingSpinner, Alert } from '@/components/ui';

function CallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [donationId, setDonationId] = useState<string | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  // Extract donation ID from URL parameters
  // Different payment gateways use different parameter names
  useEffect(() => {
    // Paystack uses 'reference', Stripe uses 'payment_intent',
    // Flutterwave uses 'transaction_id', or we use custom 'donation_id'
    const id =
      searchParams.get('donation_id') ||
      searchParams.get('reference') ||
      searchParams.get('payment_intent') ||
      searchParams.get('transaction_id') ||
      searchParams.get('trxref');

    if (id) {
      setDonationId(id);
    } else {
      // No donation ID found, redirect to failure
      navigate('/failure?error=no_donation_id');
    }
  }, [searchParams, navigate]);

  // Verify payment mutation
  const { mutate: verifyPayment, isPending: isVerifying, error: verificationError } = usePaymentVerification(donationId);

  // Poll donation status after verification
  const { data: donationStatus, error: statusError } = useDonationStatus(
    donationId,
    verificationAttempted // Only poll after verification is attempted
  );

  // Verify payment when donation ID is available
  useEffect(() => {
    if (donationId && !verificationAttempted) {
      setVerificationAttempted(true);
      verifyPayment(donationId, {
        onSuccess: (response) => {
          if (response.data.verified && response.data.donation.status === 'succeeded') {
            // Immediate success - redirect
            navigate(`/success?id=${donationId}`);
          } else if (response.data.donation.status === 'failed') {
            // Immediate failure - redirect
            navigate(`/failure?id=${donationId}`);
          }
          // Otherwise, polling will handle the status check
        },
        onError: () => {
          // Verification API call failed, but we'll still poll status
          console.log('Verification call failed, polling donation status...');
        },
      });
    }
  }, [donationId, verificationAttempted, verifyPayment, navigate]);

  // Handle polling results
  useEffect(() => {
    if (donationStatus?.data) {
      const { status } = donationStatus.data;

      if (status === 'succeeded') {
        navigate(`/success?id=${donationId}`);
      } else if (status === 'failed') {
        navigate(`/failure?id=${donationId}`);
      }
      // If still pending/processing, polling continues
    }
  }, [donationStatus, donationId, navigate]);

  // Show error if both verification and polling failed
  const hasError = verificationError && statusError;

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen">
      <div className="max-w-md mx-auto">
        <Card className="text-center">
          {hasError ? (
            <>
              <Alert variant="error" title="Verification Failed" className="mb-4">
                {verificationError?.message || 'Unable to verify your payment. Please contact support with your donation reference.'}
              </Alert>
              <div className="space-y-3 mt-4">
                <button
                  onClick={() => navigate('/')}
                  className="btn-primary w-full"
                >
                  Return to Donation Page
                </button>
                {donationId && (
                  <p className="text-sm text-gray-500">
                    Reference: <span className="font-mono font-semibold">{donationId}</span>
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <LoadingSpinner size="lg" />
              <h2 className="text-2xl font-semibold mt-6 mb-2 text-gray-900">
                {isVerifying ? 'Verifying Payment' : 'Processing Payment'}
              </h2>
              <p className="text-gray-600 mb-4">
                Please wait while we confirm your payment. This may take a few moments...
              </p>
              <p className="text-sm text-gray-500">
                Do not close this page or press the back button.
              </p>
              {donationId && (
                <p className="text-xs text-gray-400 mt-4">
                  Reference: <span className="font-mono">{donationId}</span>
                </p>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export default CallbackPage;
